<script setup lang="ts">
import type {
  FormField,
  FormFieldChoice,
  GraphData,
  GraphDataSet,
  GraphDatasetData
} from '@/models';
import Dialog from 'primevue/dialog';
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import Chart from 'primevue/chart';
import * as ChartJS from 'chart.js/auto';
import Checkbox from 'primevue/checkbox';
import zoomPlugin from 'chartjs-plugin-zoom';
import { VerticalLinePlugin } from '@/utils/chartJsHelper';
import { ButtonType, CustomButton } from '../common';
import { PUMP_GRAPH_COLORS } from '@/utils';

const props = defineProps({
  dialogLabel: {
    type: String,
    required: true
  },
  visible: {
    type: Boolean,
    required: true
  },
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  timeSeriesTimepoints: {
    type: Object as PropType<FormFieldChoice[]>,
    required: true
  }
});

const chartType = computed(() => {
  return props.field.plotType === 'Barplot' ? 'bar' : 'line';
});

const graphOptions = computed(() => {
  if (chartType.value === 'bar') {
    return {
      scales: {
        x: {
          type: 'category',
          stacked: true,
          title: {
            display: true,
            text: '',
            font: {
              size: 14,
              weight: 'bold'
            },
            color: '#333'
          }
        },
        y: {
          beginAtZero: true,
          stacked: false,
          title: {
            display: true,
            text: props.field.yAxisLabel ?? props.field.label,
            font: {
              size: 14,
              weight: 'bold'
            },
            color: '#333'
          }
        }
      },
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: true,
          labels: {
            filter: (legendItem: unknown, data: unknown) => {
              // Only show legend for datasets with a non-empty label
              //@ts-expect-error just the unknown datatypes
              return data.datasets[legendItem.datasetIndex].label !== '';
            }
          },
          generateLabels: (chart: ChartJS.Chart) => {
            return chart.data.datasets.map(
              (dataset: ChartJS.ChartDataset, index) => ({
                text: dataset.label,
                fillStyle: dataset.backgroundColor,
                strokeStyle: dataset.borderColor,
                hidden: !chart.isDatasetVisible(index),
                pointStyle:
                  dataset.label == 'Active liver' ||
                  dataset.label == 'Historical median' ?
                    'circle' :
                    'rect',
                pointStyleWidth:
                  dataset.label === 'Active liver' ||
                  dataset.label == 'Historical median' ?
                    undefined :
                    40,
                datasetIndex: index
              })
            );
          },
          usePointStyle: true
        }
      }
    };
  }

  return {
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        ticks: {
          stepSize: 1
        },
        title: {
          display: true,
          text: 'Perfusion time (hours)',
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333'
        }
      },
      y: {
        title: {
          display: true,
          text: props.field.yAxisLabel ?? props.field.label,
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333'
        }
      }
    },
    layout: {
      padding: 0
    },
    events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: 'xy'
        },
        zoom: {
          pinch: {
            enabled: true
          },
          wheel: {
            enabled: true
          },
          mode: 'xy'
        }
      },
      verticalLines: [
        {
          x: 0,
          color: 'black',
          lineWidth: 1,
          dash: [6, 6]
        }
      ],
      tooltip: {
        events: ['click', 'mousemove', 'mouseout'],
        callbacks: {
          // Customizes the title (top line of tooltip)
          title: (tooltipItems: unknown[]) => {
            const item = tooltipItems[0];
            //@ts-expect-error just the unknown datatypes
            return `Timepoint: ${Number(item.parsed.x).toFixed(2)}`;
          },
          // Customizes the label (each line in tooltip)
          label: (tooltipItem: unknown) => {
            //@ts-expect-error just the unknown datatypes
            if (tooltipItem.dataset.label === 'Sodium Bicarbonate (8.4%)') {
              //@ts-expect-error just the unknown datatypes
              return `Volume: ${tooltipItem.raw.volume}`;
            }
            //@ts-expect-error just the unknown datatypes
            if (tooltipItem.dataset.tooltipLabel) {
              //@ts-expect-error just the unknown datatypes
              return `${tooltipItem.dataset.tooltipLabel}: ${Number(tooltipItem.parsed.y).toFixed(2)}`;
            }
            //@ts-expect-error just the unknown datatypes
            return `${tooltipItem.dataset.label}: ${Number(tooltipItem.parsed.y).toFixed(2)}`;
          }
        }
      },
      legend: {
        labels: {
          filter: (legendItem: unknown, data: unknown) => {
            // Only show legend for datasets with a non-empty label
            return (
              //@ts-expect-error just the unknown datatypes
              data.datasets[legendItem.datasetIndex].label !== ''
            );
          }
        }
      }
    },
    animation: false,
    spanGaps: true,
    responsive: true,
    maintainAspectRatio: false,
    datasets: {
      line: {
        pointRadius: 4,
        pointHoverRadius: 4,
        pointHoverBorderWidth: 4,
        pointBorderColor: '#0265de',
        pointHoverBorderColor: '#0265de'
      }
    },
    elements: {
      point: {
        radius: 0
      },
      line: {
        tension: 0.4,
        borderWidth: 3,
        hoverBorderWidth: 5,
        backgroundColor: '#0265de',
        borderColor: '#0265de',
        borderCapStyle: 'round',
        borderJoinStyle: 'round'
      }
    }
  };
});

onMounted(() => {
  ChartJS.Chart.register(VerticalLinePlugin, zoomPlugin);
  prepareGraphData();
});

const chartRef = ref();

const reset = () => {
  if (chartRef.value) {
    const chart = chartRef.value.getChart();
    chart.resetZoom();

    chart.data.datasets.forEach((_dataset: unknown, index: number) => {
      chart.show(index);
    });
  }
};

const emits = defineEmits(['onClose']);

const closeDialog = async () => {
  emits('onClose');
};

const graphData = ref<GraphData>();
const showBaseLineData = ref<boolean>(false);
type HistoricalPoint = NonNullable<FormField['historicalData']>[number];

const TIMEPOINT_EPSILON = 1e-6;
const TIMEPOINT_PRECISION = 6;

const normalizeTimepoint = (timepoint: unknown): number | undefined => {
  const numeric = Number(timepoint);
  if (!Number.isFinite(numeric)) {
    return undefined;
  }

  return Math.abs(numeric) < TIMEPOINT_EPSILON ? 0 : numeric;
};

const toFiniteOrNaN = (value: unknown): number => {
  if (value === null || value === undefined || value === '') {
    return Number.NaN;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : Number.NaN;
};

const toFiniteOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const normalizeHistoricalSeries = (
  series: FormField['historicalData'] | FormField['centerHistoricData'],
  includeBaseline: boolean
): HistoricalPoint[] => {
  const normalized: HistoricalPoint[] = [];

  for (const point of series ?? []) {
    const x = normalizeTimepoint(point.perfusionActualTimepoint);
    if (x === undefined) {
      continue;
    }
    if (!includeBaseline && x < 0) {
      continue;
    }

    normalized.push({
      ...point,
      perfusionActualTimepoint: x,
      value: toFiniteOrNaN(point.value),
      ymin: toFiniteOrNaN(point.ymin),
      ymax: toFiniteOrNaN(point.ymax),
      ymin2: toFiniteOrUndefined(point.ymin2),
      ymax2: toFiniteOrUndefined(point.ymax2)
    });
  }

  normalized.sort(
    (a, b) => a.perfusionActualTimepoint - b.perfusionActualTimepoint
  );

  const dedupedByTimepoint = new Map<number, HistoricalPoint>();
  for (const point of normalized) {
    const key = Number(point.perfusionActualTimepoint.toFixed(TIMEPOINT_PRECISION));
    dedupedByTimepoint.set(key, {
      ...point,
      perfusionActualTimepoint: key
    });
  }

  return Array.from(dedupedByTimepoint.values()).sort(
    (a, b) => a.perfusionActualTimepoint - b.perfusionActualTimepoint
  );
};

watch(
  () => showBaseLineData.value,
  () => {
    reset();
    prepareGraphData();
  }
);

const prepareGraphData = () => {
  if (chartType.value === 'bar') {
    const pumpItem = props.field.historicalData?.[0];
    const centerItem = props.field.centerHistoricData?.[0];
    const hasCenterData = centerItem != null;
    const labels: string[] = hasCenterData ? ['PUMP', 'Center'] : ['PUMP'];

    // Collect active liver scatter points
    const liverPoints: GraphDatasetData[] = [];
    if (props.field.variableName === 'TOTAL_BILE_PRODUCTION') {
      let totalBile: { value: number | undefined } = { value: undefined };
      if (props.field.value) {
        try {
          totalBile = JSON.parse(props.field.value);
        } catch (e) {
          totalBile = { value: 0 };
        }
      } else {
        totalBile = { value: 0 };
      }
      const v = Number(totalBile.value);
      if (Number.isFinite(v) && v !== 0) {
        liverPoints.push({ x: 'PUMP', y: v });
        if (hasCenterData) liverPoints.push({ x: 'Center', y: v });
      }
    } else {
      props.field.timeSeriesData?.forEach(data => {
        if (data.value) {
          const v = Number(data.value);
          if (Number.isFinite(v)) {
            liverPoints.push({ x: 'PUMP', y: v });
            if (hasCenterData) liverPoints.push({ x: 'Center', y: v });
          }
        }
      });
    }

    const datasets: GraphDataSet[] = [];

    datasets.push({
      label: 'Active liver',
      type: 'scatter',
      data: liverPoints,
      backgroundColor: [PUMP_GRAPH_COLORS.ACTIVE_LIVER],
      pointRadius: 8,
      pointHoverRadius: 10
    });

    if (pumpItem) {
      const pumpValue = Number(pumpItem.value);

      datasets.push({
        label: `PUMP bar (${props.field.liverCount} livers)`,
        type: 'scatter',
        data: Number.isFinite(pumpValue) ? [{ x: 'PUMP', y: pumpValue }] : [],
        backgroundColor: [PUMP_GRAPH_COLORS.PUMP_MEAN],
        pointRadius: 8,
        pointHoverRadius: 10
      });

      // Outer range (central 95% of values) at PUMP position only
      datasets.push({
        label: '',
        data: [
          Number.isFinite(Number(pumpItem.ymin2)) && Number.isFinite(Number(pumpItem.ymax2)) ?
            [Number(pumpItem.ymin2), Number(pumpItem.ymax2)] :
            null,
          ...(hasCenterData ? [null] : [])
        ],
        backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN],
        barPercentage: 0.2
      });

      // Inner range (95% CI of the median) at PUMP position only
      datasets.push({
        label: '',
        data: [
          Number.isFinite(Number(pumpItem.ymin)) && Number.isFinite(Number(pumpItem.ymax)) ?
            [Number(pumpItem.ymin), Number(pumpItem.ymax)] :
            null,
          ...(hasCenterData ? [null] : [])
        ],
        backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN_2],
        barPercentage: 0.2
      });
    }

    if (hasCenterData) {
      const centerValue = Number(centerItem!.value);

      datasets.push({
        label: `Center bar (${props.field.centerLiverCount} livers)`,
        type: 'scatter',
        data: Number.isFinite(centerValue) ? [{ x: 'Center', y: centerValue }] : [],
        backgroundColor: [PUMP_GRAPH_COLORS.CENTER_MEAN],
        pointRadius: 8,
        pointHoverRadius: 10
      });

      // Outer range at Center position only
      datasets.push({
        label: '',
        data: [
          null,
          Number.isFinite(Number(centerItem!.ymin2)) && Number.isFinite(Number(centerItem!.ymax2)) ?
            [Number(centerItem!.ymin2), Number(centerItem!.ymax2)] :
            null
        ],
        backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN],
        barPercentage: 0.2
      });

      // Inner range at Center position only
      datasets.push({
        label: '',
        data: [
          null,
          Number.isFinite(Number(centerItem!.ymin)) && Number.isFinite(Number(centerItem!.ymax)) ?
            [Number(centerItem!.ymin), Number(centerItem!.ymax)] :
            null
        ],
        backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN_2],
        barPercentage: 0.2
      });
    }

    graphData.value = {
      labels,
      datasets
    };
    return;
  }

  const values: (GraphDatasetData | null)[] = [];
  if (props.field.cumulativeTimeSeriesData) {
    props.field.cumulativeTimeSeriesData.forEach(data => {
      values.push({
        x: Number(data.perfusionActualTimepoint),
        y: data.value
      });
    });
  } else {
    props.field.timeSeriesData?.forEach(data => {
      if (data.value) {
        values.push({
          x: Number(data.perfusionActualTimepoint),
          y: data.value
        });
      }
    });
  }

  const dataset: GraphDataSet = {
    label: 'Active liver',
    tooltipLabel: 'Active liver',
    data: values,
    backgroundColor: [PUMP_GRAPH_COLORS.ACTIVE_LIVER],
    borderColor: [PUMP_GRAPH_COLORS.ACTIVE_LIVER]
  };

  const pumpValues: GraphDatasetData[] = [];
  const pumpMinValues: GraphDatasetData[] = [];
  const pumpMaxValues: GraphDatasetData[] = [];
  const pumpMinValues2: GraphDatasetData[] = [];
  const pumpMaxValues2: GraphDatasetData[] = [];

  const pumpHistoricalSeries = normalizeHistoricalSeries(
    props.field.historicalData,
    showBaseLineData.value
  );
  for (const data of pumpHistoricalSeries) {
    pumpValues.push({
      x: Number(data.perfusionActualTimepoint),
      y: data.value
    });
    pumpMinValues.push({
      x: Number(data.perfusionActualTimepoint),
      y: data.ymin
    });
    pumpMaxValues.push({
      x: Number(data.perfusionActualTimepoint),
      y: data.ymax
    });
    if (data.ymin2 !== null && data.ymin2 !== undefined && data.ymax2 !== null && data.ymax2 !== undefined) {
      pumpMinValues2.push({
        x: Number(data.perfusionActualTimepoint),
        y: data.ymin2
      });
      pumpMaxValues2.push({
        x: Number(data.perfusionActualTimepoint),
        y: data.ymax2
      });
    }
  }

  const pumpLiverDataset: GraphDataSet = {
    label: `PUMP curve (${props.field.liverCount} livers)`,
    tooltipLabel: 'Mean',
    data: pumpValues,
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_MEAN],
    borderColor: [PUMP_GRAPH_COLORS.PUMP_MEAN],
    pointRadius: 0,
    pointHoverRadius: 4,
    pointBorderColor: '#bf02ef',
    pointHoverBorderColor: '#bf02ef'
  };

  const minDataset: GraphDataSet = {
    label: '',
    tooltipLabel: '95% CI - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: pumpMinValues,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN],
    fill: '+1'
  };

  const maxDataset: GraphDataSet = {
    label: '',
    tooltipLabel: '95% CI - high',
    data: pumpMaxValues,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN],
    fill: false
  };

  const minDataset2: GraphDataSet = {
    label: '',
    tooltipLabel: '95% data - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: pumpMinValues2,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN_2],
    fill: '+1'
  };

  const maxDataset2: GraphDataSet = {
    label: '',
    tooltipLabel: '95% data - high',
    data: pumpMaxValues2,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.PUMP_YMIN_2],
    fill: false
  };

  const markerPoints: GraphDatasetData[] = [];

  props.field.bicarbonateData?.forEach(data => {
    markerPoints.push({
      x: data.perfusionActualTimepoint!,
      y: data.value,
      //@ts-expect-error: we are using a custom property
      volume: data.volume
    });
  });

  const markerDataset: GraphDataSet = {
    label: 'Sodium Bicarbonate (8.4%)',
    type: 'scatter',
    data: markerPoints,
    backgroundColor: ['#ff0000'],
    borderColor: ['#ff0000'],
    pointRadius: 8
  };

  const datasets = [dataset, pumpLiverDataset, minDataset, maxDataset];

  if (pumpMinValues2.length > 0 && pumpMaxValues2.length > 0) {
    datasets.push(minDataset2);
    datasets.push(maxDataset2);
  }

  if (props.field.bicarbonateData && props.field.bicarbonateData.length > 0) {
    datasets.push(markerDataset);
  }

  const centerValues: GraphDatasetData[] = [];
  const centerMinValues: GraphDatasetData[] = [];
  const centerMaxValues: GraphDatasetData[] = [];
  const centerMinValues2: GraphDatasetData[] = [];
  const centerMaxValues2: GraphDatasetData[] = [];

  const centerHistoricalSeries = normalizeHistoricalSeries(
    props.field.centerHistoricData,
    showBaseLineData.value
  );
  for (const data of centerHistoricalSeries) {
    centerValues.push({
      x: Number(data.perfusionActualTimepoint),
      y: data.value
    });
    centerMinValues.push({
      x: Number(data.perfusionActualTimepoint),
      y: data.ymin
    });
    centerMaxValues.push({
      x: Number(data.perfusionActualTimepoint),
      y: data.ymax
    });
    if (data.ymin2 !== null && data.ymin2 !== undefined && data.ymax2 !== null && data.ymax2 !== undefined) {
      centerMinValues2.push({
        x: Number(data.perfusionActualTimepoint),
        y: data.ymin2
      });
      centerMaxValues2.push({
        x: Number(data.perfusionActualTimepoint),
        y: data.ymax2
      });
    }
  }

  const centerLiverDataset: GraphDataSet = {
    label: `Center curve (${props.field.centerLiverCount} livers)`,
    tooltipLabel: 'Mean',
    data: centerValues,
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_MEAN],
    borderColor: [PUMP_GRAPH_COLORS.CENTER_MEAN],
    pointRadius: 0,
    pointHoverRadius: 4,
    pointBorderColor: '#bf02ef',
    pointHoverBorderColor: '#bf02ef'
  };

  const centerMinDataset: GraphDataSet = {
    label: '',
    tooltipLabel: '95% CI - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: centerMinValues,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN],
    fill: '+1'
  };

  const centerMaxDataset: GraphDataSet = {
    label: '',
    tooltipLabel: '95% CI - high',
    data: centerMaxValues,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN],
    fill: false
  };

  const centerMinDataset2: GraphDataSet = {
    label: '',
    tooltipLabel: '95% data - low',
    pointRadius: 0,
    pointHoverRadius: 0,
    data: centerMinValues2,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN_2],
    fill: '+1'
  };

  const centerMaxDataset2: GraphDataSet = {
    label: '',
    tooltipLabel: '95% data - high',
    data: centerMaxValues2,
    pointRadius: 0,
    pointHoverRadius: 0,
    borderColor: ['rgba(0,0,0,0)'],
    backgroundColor: [PUMP_GRAPH_COLORS.CENTER_YMIN_2],
    fill: false
  };

  if (centerLiverDataset.data.length > 0) {
    datasets.push(centerLiverDataset);
  }

  if (centerMinDataset.data.length > 0 && centerMaxDataset.data.length > 0) {
    datasets.push(centerMinDataset);
    datasets.push(centerMaxDataset);
  }

  if (centerMinValues2.length > 0 && centerMaxValues2.length > 0) {
    datasets.push(centerMinDataset2);
    datasets.push(centerMaxDataset2);
  }

  graphData.value = {
    labels: [],
    datasets
  };
};
</script>

<template>
  <Dialog :visible="visible"
    modal
    :header="dialogLabel"
    class="data-graph-dialog"
    :closeOnEscape="true"
    :draggable="false"
    style="width: 100%; max-width: 1100px; margin: 8px; overflow-x: auto"
    @update:visible="closeDialog">
    <div class="chart-root" v-if="graphData">
      <div class="chart-container">
        <Chart :type="chartType"
          ref="chartRef"
          :data="graphData"
          :options="graphOptions"
          class="chart-canvas"/>
      </div>

      <div class="group" v-if="chartType === 'line'">
        <div class="baseline-label">Show baseline data</div>
        <Checkbox v-model="showBaseLineData"
          binary
          inputId="baseline"
          name="baseline"
          value="baseline"/>
      </div>

      <CustomButton :text="'Reset'"
        @onClick="reset"
        v-if="chartType === 'line'"
        class="reset-zoom-button"
        :type="ButtonType.OUTLINED"/>
    </div>
  </Dialog>
</template>

<style lang="scss">
@media (orientation: landscape) and (max-height: 500px) {
  /* Make the dialog fullscreen */
  .p-dialog.data-graph-dialog {
    width: 100svw !important;
    height: 100svh !important;
    max-height: 100svh !important;
    top: 0 !important;
    left: 0 !important;
    margin: 0 !important;
    overflow-y: auto !important;
  }

  .data-graph-dialog {
    .p-dialog-content {
      overflow-y: visible !important; /* Allow content to overflow */
      flex-grow: 0;
    }
  }
}
@media (orientation: portrait) and (max-width: 500px) {
  .data-graph-dialog {
    .p-dialog-content {
      padding-left: 8px !important;
      padding-right: 8px !important;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
<style lang="scss" scoped>
.chart-root {
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  overflow-x: auto;
  overflow-y: hidden;
}
.chart-container {
  position: relative;
  width: 100%;
  max-width: 1000px;
  /* Using padding-top to enforce a 16:9 aspect ratio. Adjust as needed. */
  /* 56.25% = 9 / 16 */
  padding-top: 56.25%;
}

@media (orientation: portrait) and (max-width: 500px) {
  .chart-container {
    padding-top: 100.25%;
  }
}

:deep(.p-chart) {
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
:deep(.p-dialog-content) {
  padding: 0 !important;

}
.reset-zoom-button {
  margin-top: 16px;
  align-self: center;
}
@media (orientation: landscape) and (max-height: 500px) {
  .chart-root {
    padding-left: 16px;
    padding-right: 16px;
    /* Ensure it doesn't try to center vertically, allowing it to sit at the top */
    justify-content: flex-start;
  }

  .chart-container {
    padding-top: 0;
    /* Make the chart taller than the screen to encourage scrolling */
    height: 80vh;
  }
}
:deep(.p-chart canvas) {
  touch-action: none;
}
.group {
  display: flex;
  align-items: center;
  margin-top: 10px;

  .baseline-label {
    margin-right: 10px;
    font-weight: bold;
  }
}
</style>
